import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  ShoppingCart, ShoppingBag,
  Sun,
  Moon,
  Home,
  Palette,
  Image,
  Ticket,
  Store,
  Users,
  ArrowRight,
  User,
  LogOut,
  Heart,
  Package,
  UserCircle,
  Mail,
  Sparkles,
  Bell,
  Truck,
  RefreshCw,
  CheckCheck,
  MessageSquare,
  Newspaper,
  Phone,
  Shield,
  Cookie,
  HelpCircle,
} from 'lucide-react';
import { useAppStore, useCartStore } from '@/lib/store';
import { useUserStore } from '@/lib/user-store';
import { useAdminStore } from '@/lib/admin-store';
import { useMenuLabel } from '@/lib/menu-labels';
import { useTranslation } from '@/hooks/useTranslation';

/* ====== Magnetic Shop Button ====== */
const ShopButton: React.FC<{ label: string; active: boolean }> = ({ label, active }) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });
  const rotateX = useTransform(springY, [-8, 8], [6, -6]);
  const rotateY = useTransform(springX, [-8, 8], [-6, 6]);

  const handleMouse = (e: React.MouseEvent): void => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * 0.15);
    y.set((e.clientY - cy) * 0.15);
  };

  const handleLeave = (): void => {
    x.set(0);
    y.set(0);
  };

  return (
    <Link to="/shop">
      <motion.div
        ref={ref}
        onMouseMove={handleMouse}
        onMouseLeave={handleLeave}
        style={{ x: springX, y: springY, rotateX, rotateY }}
        className="relative group cursor-pointer"
      >
        {/* Animated rotating border */}
        <div className="absolute -inset-[2px] rounded-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              background: 'conic-gradient(from var(--angle, 0deg), transparent 0%, rgba(0,0,0,0.15) 25%, transparent 50%, rgba(0,0,0,0.15) 75%, transparent 100%)',
              animation: 'spin-border 3s linear infinite',
            }}
          />
        </div>

        {/* Glow pulse */}
        <div className="absolute -inset-3 rounded-3xl bg-black/5 dark:bg-white/5 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />

        {/* Button body */}
        <div
          className={`relative flex items-center gap-3 pl-5 pr-6 py-3 rounded-2xl overflow-hidden transition-all duration-300 ${
            active
              ? 'bg-black dark:bg-white text-white dark:text-black shadow-xl'
              : 'bg-black dark:bg-white text-white dark:text-black shadow-lg group-hover:shadow-2xl'
          }`}
        >
          {/* Shimmer sweep */}
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[800ms] ease-out" />

          {/* Inner glow top edge */}
          <span className="absolute top-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

          <Store size={17} strokeWidth={2.5} className="relative z-10" />
          <span className="relative z-10 text-[10px] font-bold uppercase tracking-[0.25em]">
            {label}
          </span>
          <ArrowRight
            size={14}
            strokeWidth={2.5}
            className="relative z-10 -mr-1 transition-transform duration-300 group-hover:translate-x-1"
          />
        </div>
      </motion.div>
    </Link>
  );
};

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { isDarkMode, toggleTheme, language, setLanguage, currency, setCurrency } = useAppStore();
  const { toggleCart, totalItems } = useCartStore();
  const currentUser = useUserStore((s) => s.currentUser);
  const logoutUser = useUserStore((s) => s.logout);
  const markAllNotificationsRead = useUserStore((s) => s.markAllNotificationsRead);
  const { t, localize } = useTranslation();
  const cartCount = totalItems();

  // Pages with full-bleed hero images need a visible navbar background
  const hasHeroImage = /^\/(exhibitions|art-fairs)\/[^/]+$/.test(location.pathname);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const conversations = useAdminStore((s) => s.conversations);
  const menuVis = useAdminStore((s) => s.settings?.menuVisibility) ?? { home: true, shop: true, artists: true, exhibitions: true, artFairs: true, dropshop: true, news: true, contact: true, team: true, faq: true };
  const menuLabel = useMenuLabel();
  const notifications = currentUser?.notifications ?? [];
  // Count unread admin messages from API-stored conversations
  const unreadMessageCount = currentUser
    ? (conversations ?? [])
        .filter((c) => c.userId === currentUser._id || c.guestEmail?.toLowerCase() === currentUser.email.toLowerCase())
        .reduce((count, c) => count + (c.messages ?? []).filter((m) => m.senderType === 'admin' && !m.read).length, 0)
    : 0;
  const unreadCount = notifications.filter((n) => !n.read).length + unreadMessageCount;

  useEffect(() => {
    const handleScroll = (): void => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setUserMenuOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handleClick = (e: MouseEvent): void => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notifOpen]);

  // Desktop nav — no Home (logo is home link)
  const navLinks = [
    menuVis.artists !== false && { label: menuLabel('artists', t.nav.artists), href: '/artists', icon: Palette },
    menuVis.exhibitions !== false && { label: menuLabel('exhibitions', t.nav.exhibitions), href: '/exhibitions', icon: Image },
    menuVis.artFairs !== false && { label: menuLabel('artFairs', t.nav.artFairs), href: '/art-fairs', icon: Ticket },
    menuVis.dropshop !== false && { label: menuLabel('dropshop', t.nav.dropshop), href: '/dropshop', icon: Sparkles },
  ].filter(Boolean) as Array<{ label: string; href: string; icon: React.ElementType }>;

  const isActive = (href: string): boolean => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const shopActive = isActive('/shop') || isActive('/artwork') || isActive('/checkout');

  const ICON_BTN = "w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/60 dark:bg-white/[0.07] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.08] flex items-center justify-center text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-all duration-300 shadow-sm";

  const timeAgo = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t.notifications?.justNow ?? 'Just now';
    if (mins < 60) return `${mins} ${t.notifications?.minutesAgo ?? 'min ago'}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} ${t.notifications?.hoursAgo ?? 'hr ago'}`;
    const days = Math.floor(hrs / 24);
    return `${days} ${t.notifications?.daysAgo ?? 'd ago'}`;
  };

  return (
    <>
      {/* Keyframes for rotating border */}
      <style>{`
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes spin-border {
          to { --angle: 360deg; }
        }
      `}</style>

      {/* ====== TOP BAR ====== */}
      <div
        className={`fixed top-0 left-0 w-full z-[500] flex items-center pointer-events-none transition-all duration-500 ${
          scrolled || hasHeroImage
            ? 'px-3 py-2.5 sm:px-4 sm:py-3 md:px-6 md:py-3 bg-white/70 dark:bg-black/70 backdrop-blur-xl'
            : 'px-4 py-4 sm:px-6 sm:py-5 md:px-10 md:py-7'
        }`}
        style={scrolled || hasHeroImage ? { WebkitBackdropFilter: 'blur(20px)' } : undefined}
      >
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          className="pointer-events-auto cursor-pointer shrink-0"
          onClick={() => navigate('/')}
        >
          <span className="text-black dark:text-white font-syncopate font-bold text-sm sm:text-lg md:text-xl tracking-tighter logo-brand">
            FOTOATELIER.
          </span>
        </motion.div>

        {/* Spacer — pushes buttons to the right */}
        <div className="flex-1" />

        {/* ====== CENTER: Floating nav links (desktop) ====== */}
        <div className="hidden nav:flex items-center justify-center pointer-events-auto absolute left-[43%] -translate-x-1/2">
          <div className="flex items-center gap-1">
            {navLinks.map((link, i) => {
              const active = isActive(link.href);
              const Icon = link.icon;
              return (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: -15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.2 + i * 0.06,
                    ease: [0.76, 0, 0.24, 1],
                  }}
                >
                  <Link to={link.href} className="group relative flex flex-col items-center">
                    <div
                      className={`flex items-center justify-center gap-2.5 px-3 xl:px-5 py-2.5 rounded-2xl transition-all duration-300 whitespace-nowrap ${
                        active
                          ? 'bg-black/[0.07] dark:bg-white/[0.08] text-black dark:text-white'
                          : 'text-black/50 dark:text-white/50 hover:text-black/90 dark:hover:text-white/90 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                      }`}
                      title={link.label}
                    >
                      <Icon
                        size={17}
                        strokeWidth={active ? 2.5 : 2}
                        className="transition-all duration-300 shrink-0"
                      />
                      <span
                        className={`hidden xl:inline text-[10px] font-bold uppercase tracking-[0.18em] transition-all duration-300 ${
                          active ? 'opacity-100' : 'opacity-80 group-hover:opacity-100'
                        }`}
                      >
                        {link.label}
                      </span>
                    </div>
                    {/* Active indicator */}
                    <motion.div
                      className="h-[3px] rounded-full bg-black dark:bg-white"
                      initial={false}
                      animate={{
                        width: active ? 20 : 0,
                        opacity: active ? 1 : 0,
                      }}
                      transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
                    />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ====== RIGHT: Shop CTA + Action buttons ====== */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
          className="flex items-center gap-2 sm:gap-3 pointer-events-auto shrink-0"
        >
          {/* SHOP — visible from lg (1024px) where BottomNav hides */}
          {menuVis.shop !== false && (
            <div className="hidden lg:block">
              <ShopButton label={menuLabel('shop', t.nav.shop)} active={shopActive} />
            </div>
          )}

          {/* Cart — next to shop button */}
          <button
            onClick={toggleCart}
            className={`relative ${ICON_BTN}`}
          >
            <ShoppingCart size={15} />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span
                  key={cartCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: [1.3, 1] }}
                  exit={{ scale: 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 15 }}
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px] px-1 bg-black dark:bg-white text-white dark:text-black text-[8px] sm:text-[9px] font-bold flex items-center justify-center rounded-full shadow-md"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          {/* Divider between shop/cart and utility buttons (desktop) */}
          <div className="hidden nav:block w-px h-6 bg-black/8 dark:bg-white/8" />

          {/* Language (desktop only) */}
          <button
            onClick={() => setLanguage(language === 'en' ? 'ka' : 'en')}
            className={`hidden md:flex ${ICON_BTN}`}
            title={language === 'en' ? 'ქართული' : 'English'}
          >
            <span className="text-[9px] font-bold uppercase tracking-wider">
              {language === 'en' ? 'KA' : 'EN'}
            </span>
          </button>

          {/* Theme (desktop only) */}
          <button
            onClick={toggleTheme}
            className={`hidden md:flex ${ICON_BTN}`}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={isDarkMode ? 'sun' : 'moon'}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center"
              >
                {isDarkMode ? <Sun size={15} /> : <Moon size={15} />}
              </motion.span>
            </AnimatePresence>
          </button>

          {/* User */}
          <div className="relative" ref={userMenuRef}>
            {currentUser ? (
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black dark:bg-white text-white dark:text-black flex items-center justify-center text-[10px] sm:text-[11px] font-bold font-syncopate shadow-sm hover:opacity-90 transition-all duration-300"
              >
                {currentUser.firstName.charAt(0).toUpperCase()}
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className={ICON_BTN}
              >
                <User size={15} />
              </button>
            )}

            {/* User dropdown */}
            <AnimatePresence>
              {userMenuOpen && currentUser && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-12 w-56 bg-white dark:bg-[#111] border border-black/[0.06] dark:border-white/[0.08] rounded-2xl shadow-xl overflow-hidden z-[600]"
                >
                  <div className="px-4 py-3 border-b border-black/5 dark:border-white/5">
                    <p className="text-[11px] font-bold truncate">{currentUser.firstName} {currentUser.lastName}</p>
                    <p className="text-[9px] text-black/40 dark:text-white/40 truncate">{currentUser.email}</p>
                  </div>
                  <div className="py-1">
                    {[
                      { label: t.profile.title, icon: <UserCircle size={14} />, href: '/profile' },
                      { label: t.profile.orders, icon: <Package size={14} />, href: '/profile?tab=orders' },
                      { label: t.profile.wishlist, icon: <Heart size={14} />, href: '/profile?tab=wishlist' },
                    ].map((item, i) => (
                      <button
                        key={i}
                        onClick={() => { setUserMenuOpen(false); navigate(item.href); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-all"
                      >
                        {item.icon} {item.label}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-black/5 dark:border-white/5 py-1">
                    <button
                      onClick={() => { setUserMenuOpen(false); logoutUser(); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-red-500/70 hover:text-red-500 hover:bg-red-500/5 transition-all"
                    >
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Notifications bell */}
          {currentUser && (
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }}
                className={`relative ${ICON_BTN}`}
              >
                <Bell size={15} />
                <AnimatePresence>
                  {unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px] px-1 bg-red-500 text-white text-[8px] sm:text-[9px] font-bold flex items-center justify-center rounded-full shadow-md"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              <AnimatePresence>
                {notifOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-12 w-72 sm:w-80 bg-white dark:bg-[#111] border border-black/[0.06] dark:border-white/[0.08] rounded-2xl shadow-xl overflow-hidden z-[600]"
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 dark:border-white/5">
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em]">{t.notifications?.title ?? 'Notifications'}</span>
                      {unreadCount > 0 && (
                        <button
                          onClick={() => markAllNotificationsRead()}
                          className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white transition-colors"
                        >
                          <CheckCheck size={11} />
                          {t.notifications?.markAllRead ?? 'Mark all read'}
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {(() => {
                        // Build combined notification items from localStorage notifications + API conversation messages
                        const items: Array<{ key: string; type: string; message: string; time: string; read: boolean; href: string }> = [];
                        // From localStorage notifications
                        notifications.forEach((notif) => {
                          items.push({
                            key: notif._id,
                            type: notif.type,
                            message: localize(notif.message),
                            time: notif.createdAt,
                            read: notif.read,
                            href: notif.type === 'message_received' ? '/profile?tab=messages' : '/profile?tab=orders',
                          });
                        });
                        // From API conversations — unread admin messages
                        if (currentUser) {
                          (conversations ?? [])
                            .filter((c) => c.userId === currentUser._id || c.guestEmail?.toLowerCase() === currentUser.email.toLowerCase())
                            .forEach((c) => {
                              (c.messages ?? []).filter((m) => m.senderType === 'admin' && !m.read).forEach((m) => {
                                items.push({
                                  key: `msg-${m._id}`,
                                  type: 'message_received',
                                  message: `${t.inbox?.adminReplied ?? 'Admin replied'}: "${m.content.slice(0, 50)}${m.content.length > 50 ? '...' : ''}"`,
                                  time: m.createdAt,
                                  read: false,
                                  href: '/profile?tab=messages',
                                });
                              });
                            });
                        }
                        // Sort by time descending
                        items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());

                        if (items.length === 0) {
                          return (
                            <div className="py-10 text-center">
                              <Bell size={24} className="mx-auto text-black/10 dark:text-white/10 mb-2" />
                              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
                                {t.notifications?.noNotifications ?? 'No notifications'}
                              </p>
                            </div>
                          );
                        }

                        return items.slice(0, 15).map((item) => (
                          <button
                            key={item.key}
                            onClick={() => { setNotifOpen(false); navigate(item.href); }}
                            className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-all border-b border-black/[0.03] dark:border-white/[0.03] last:border-0 ${
                              !item.read ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                              item.type === 'tracking_added'
                                ? 'bg-indigo-500/10 text-indigo-500'
                                : item.type === 'message_received'
                                  ? 'bg-green-500/10 text-green-500'
                                  : 'bg-blue-500/10 text-blue-500'
                            }`}>
                              {item.type === 'tracking_added' ? <Truck size={13} /> : item.type === 'message_received' ? <MessageSquare size={13} /> : <RefreshCw size={13} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-[11px] leading-relaxed ${!item.read ? 'font-bold' : 'font-medium text-black/60 dark:text-white/60'}`}>
                                {item.message}
                              </p>
                              <p className="text-[9px] text-black/30 dark:text-white/30 mt-0.5">{timeAgo(item.time)}</p>
                            </div>
                            {!item.read && (
                              <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2" />
                            )}
                          </button>
                        ));
                      })()}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Hamburger (mobile only) */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Menu"
            className={`nav:hidden ${ICON_BTN} flex-col gap-[4px] sm:gap-[5px]`}
          >
            <motion.span
              animate={isOpen ? { rotate: 45, y: 5.5 } : { rotate: 0, y: 0 }}
              className="block w-3.5 sm:w-4 h-[1.5px] bg-current origin-center transition-colors"
            />
            <motion.span
              animate={isOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
              className="block w-3.5 sm:w-4 h-[1.5px] bg-current origin-center transition-colors"
            />
            <motion.span
              animate={isOpen ? { rotate: -45, y: -5.5 } : { rotate: 0, y: 0 }}
              className="block w-3.5 sm:w-4 h-[1.5px] bg-current origin-center transition-colors"
            />
          </button>
        </motion.div>
      </div>

      {/* ====== FULLSCREEN MENU ====== */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[450] bg-white dark:bg-black flex flex-col overflow-hidden"
          >
            {/* Decorative circles */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-black/[0.03] dark:border-white/[0.03]"
            />
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-black/[0.03] dark:border-white/[0.03]"
            />

            {/* Menu links */}
            <div className="flex-1 flex flex-col relative z-10 w-full max-w-sm mx-auto px-4 sm:px-6 pt-20 pb-4 overflow-y-auto">
              <div className="flex flex-col gap-1 w-full">
                {([
                  menuVis.home !== false && { label: menuLabel('home', t.nav.home), href: '/', icon: Home },
                  menuVis.dropshop !== false && { label: menuLabel('dropshop', t.nav.dropshop), href: '/dropshop', icon: Sparkles },
                  menuVis.news !== false && { label: menuLabel('news', language === 'ka' ? 'სიახლეები' : 'News'), href: '/news', icon: Newspaper },
                  menuVis.contact !== false && { label: menuLabel('contact', t.nav.contact), href: '/contact', icon: Phone },
                  menuVis.team !== false && { label: menuLabel('team', t.nav.team), href: '/team', icon: Users },
                  menuVis.faq !== false && { label: menuLabel('faq', 'FAQ'), href: '/faq', icon: HelpCircle },
                ].filter(Boolean) as Array<{ label: string; href: string; icon: React.ElementType }>).map((link, i) => {
                  const active = isActive(link.href);
                  const Icon = link.icon;
                  return (
                    <motion.div
                      key={link.href}
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{
                        delay: i * 0.05,
                        duration: 0.4,
                        ease: [0.76, 0, 0.24, 1],
                      }}
                    >
                      <button
                        onClick={() => navigate(link.href)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group ${
                          active
                            ? 'bg-black/[0.06] dark:bg-white/[0.06] text-black dark:text-white'
                            : 'text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.03]'
                        }`}
                      >
                        <span
                          className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 ${
                            active
                              ? 'bg-black/[0.06] dark:bg-white/[0.06]'
                              : 'bg-black/[0.04] dark:bg-white/[0.04] group-hover:bg-black/[0.08] dark:group-hover:bg-white/[0.08]'
                          }`}
                        >
                          <Icon size={17} strokeWidth={active ? 2.5 : 1.5} />
                        </span>
                        <span className="text-sm font-syncopate font-bold uppercase tracking-tight">
                          {link.label}
                        </span>
                        {active && (
                          <motion.span
                            initial={{ x: -5, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            className="ml-auto"
                          >
                            <ArrowRight size={16} strokeWidth={2} />
                          </motion.span>
                        )}
                      </button>
                    </motion.div>
                  );
                })}
              </div>

              {/* Divider */}
              <div className="my-3 border-t border-black/[0.04] dark:border-white/[0.04]" />

              {/* Secondary links */}
              <div className="flex flex-col gap-0.5 w-full">
                {[
                  { label: menuLabel('faq', 'FAQ'), href: '/faq', icon: HelpCircle },
                  { label: 'Privacy Policy', href: '/privacy-policy', icon: Shield },
                  { label: 'Cookie Policy', href: '/cookie-policy', icon: Cookie },
                ].map((link, i) => (
                  <motion.div
                    key={link.href}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{
                      delay: 0.2 + i * 0.04,
                      duration: 0.4,
                      ease: [0.76, 0, 0.24, 1],
                    }}
                  >
                    <button
                      onClick={() => navigate(link.href)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-all duration-300"
                    >
                      <link.icon size={15} strokeWidth={1.5} />
                      <span className="text-xs font-bold uppercase tracking-wider">
                        {link.label}
                      </span>
                    </button>
                  </motion.div>
                ))}
              </div>

              {/* Divider */}
              <div className="my-3 border-t border-black/[0.04] dark:border-white/[0.04]" />

              {/* Language + Theme settings */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                className="flex items-center gap-2 px-4"
              >
                <button
                  onClick={() => setLanguage(language === 'en' ? 'ka' : 'en')}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-black/[0.04] dark:bg-white/[0.04] hover:bg-black/[0.08] dark:hover:bg-white/[0.08] transition-all"
                >
                  <span className="text-[9px] font-bold uppercase tracking-wider text-black/50 dark:text-white/50">
                    {language === 'en' ? 'KA' : 'EN'}
                  </span>
                </button>
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-black/[0.04] dark:bg-white/[0.04] hover:bg-black/[0.08] dark:hover:bg-white/[0.08] transition-all"
                >
                  {isDarkMode ? <Sun size={13} className="text-white/50" /> : <Moon size={13} className="text-black/50" />}
                </button>
              </motion.div>

              {/* Footer */}
              <div className="mt-auto pt-4">
                <p className="text-center text-[8px] font-bold uppercase tracking-[0.3em] text-black/15 dark:text-white/15">
                  Gallery Fotoatelier &middot; Tbilisi
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
